import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { EMPTY_SPEC, LAST_EDIT_STEP } from "./onboardingModel";
import { splitFocus } from "./IndustryFocusPicker";

const INITIAL = {
  website: "", companyName: "", products: "",
  analysis: null, spec: null, bestCustomers: [], intakeItems: [],
  revenueGoal: "", goalEndDate: "", averageDealSize: "", mission: "", chat: {},
  preview: null, huntJobId: null,
};

const pollJob = async (url, onTick, { interval = 2000, timeout = 150000 } = {}) => {
  const started = Date.now();
  while (Date.now() - started < timeout) {
    const { data } = await axios.get(url);
    onTick?.(data);
    if (data.status === "done") return data;
    if (data.status === "failed") throw new Error(data.error || "Step failed");
    await new Promise((r) => setTimeout(r, interval));
  }
  throw new Error("Timed out");
};

// What the setup guide says after a preview run — mirrors the on-page result so the user can answer in the chat.
const previewMessages = (result) => {
  if (!result) return [];
  if (result.blocked) {
    const down = Object.values(result.blocked.checks || {}).filter((c) => !c.ok && c.required !== false).map((c) => `${c.label}: ${c.detail}`);
    return [{ role: "assistant", fromPreview: true, content: `I stopped the preview — ${down.join("; ")}. I won't show unverified profiles as prospects. Fix the source and check again, or run anyway with what's available.`, suggestions: ["What does this mean?"] }];
  }
  const n = (result.prospects || []).length;
  if (n >= 3) {
    const names = result.prospects.map((p) => `${p.personName || "a buyer"} at ${p.companyName}`).join(", ");
    return [{ role: "assistant", fromPreview: true, content: `Found ${n} verified ${n === 1 ? "match" : "matches"}: ${names}. Each firm sits inside your industries and size range, and each title is inside your personas. If any feel off, tell me which and why.`, suggestions: ["These are too small", "Wrong job titles", "Looks right"] }];
  }
  const g = result.guidance;
  if (g) {
    return [{ role: "assistant", fromPreview: true, content: `${g.hint ? `${g.hint} ` : ""}${g.question}`, suggestions: (g.suggestions || []).slice(0, 3).map((s) => s.label) }];
  }
  return [{ role: "assistant", fromPreview: true, content: n ? `Only ${n} verified ${n === 1 ? "match" : "matches"} on the first pass. Tell me what to widen — industries, titles, geography or size — and I'll update the targeting.` : "No verified matches on the first pass. Tell me what to widen — industries, titles, geography or size — and I'll update the targeting.", suggestions: ["Add adjacent industries", "Relax the size range", "Add more buyer titles"] }];
};

export const useOnboarding = ({ userId, instanceId, instanceName }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ ...INITIAL, companyName: instanceName || "" });
  const [ready, setReady] = useState(false);
  const [job, setJob] = useState(null);           // { kind, stage, progress }
  const [busy, setBusy] = useState(false);
  const saveTimer = useRef(null);

  const patch = useCallback((p) => setData((prev) => ({ ...prev, ...(typeof p === "function" ? p(prev) : p) })), []);
  const patchSpec = useCallback((p) => setData((prev) => ({ ...prev, spec: { ...(prev.spec || EMPTY_SPEC), ...(typeof p === "function" ? p(prev.spec || EMPTY_SPEC) : p) } })), []);

  useEffect(() => {
    if (!userId || userId === "new") { setReady(true); return; }
    axios.get(`${API}/onboarding/draft/${userId}`, { params: instanceId ? { instanceId } : {} })
      .then(({ data: d }) => {
        if (d?.data && Object.keys(d.data).length) {
          setData((prev) => ({ ...prev, ...d.data, preview: null, huntJobId: null }));
          setStep(Math.min(d.step || 0, LAST_EDIT_STEP));
        }
      })
      .catch(() => {})
      .finally(() => setReady(true));
  }, [userId, instanceId]);

  useEffect(() => {
    if (!ready || !userId || userId === "new" || step > LAST_EDIT_STEP) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const { preview, huntJobId, ...persist } = data;
      axios.put(`${API}/onboarding/draft/${userId}`, { instanceId: instanceId || null, step, data: persist }).catch(() => {});
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [data, step, ready, userId, instanceId]);

  const runJob = async (kind, start, onDone, { timeout } = {}) => {
    setBusy(true);
    setJob({ kind, stage: "Starting", progress: 1 });
    try {
      const { data: { jobId } } = await start();
      const done = await pollJob(`${API}/onboarding/job/${jobId}`, (j) => setJob({ kind, stage: j.stage, progress: j.progress }), timeout ? { timeout } : {});
      onDone(done.result);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Something went wrong");
      return false;
    } finally {
      setBusy(false);
      setJob(null);
    }
  };

  const analyze = () => runJob("analyze",
    () => axios.post(`${API}/onboarding/analyze/${userId}`, { website: data.website || null, companyName: data.companyName || null, products: data.products || null }),
    (result) => patch((prev) => ({
      analysis: result,
      companyName: prev.companyName || result.companyName || "",
      products: prev.products || (result.sellerIntelligence?.productsList || []).join(", "),
      spec: { ...EMPTY_SPEC, ...result.targetSpec, ...splitFocus(result.targetSpec?.industries || []), lookalikeSeeds: prev.bestCustomers.length ? prev.bestCustomers : (result.detectedCustomers || []) },
      bestCustomers: prev.bestCustomers.length ? prev.bestCustomers : (result.detectedCustomers || []),
    })));

  const runPreview = ({ skipPreflight = false } = {}) => runJob("preview",
    () => axios.post(`${API}/onboarding/preview/${userId}`, { companyName: data.companyName, products: data.products, targetSpec: { ...data.spec, lookalikeSeeds: data.bestCustomers }, skipPreflight }),
    (result) => patch((prev) => ({ preview: result, chat: { ...prev.chat, preview: [...(prev.chat?.preview || []).filter((m) => !m.fromPreview), ...previewMessages(result)] } })),
    { timeout: 200000 });

  const applyGuidance = async (suggestions, freeText) => {
    try {
      const { data: res } = await axios.post(`${API}/onboarding/apply-guidance/${userId}`, { targetSpec: { ...data.spec, lookalikeSeeds: data.bestCustomers }, suggestions, freeText: freeText || null });
      patchSpec(res.targetSpec);
      toast.success(`Targeting updated: ${res.applied.join("; ")}`);
      return runJob("preview",
        () => axios.post(`${API}/onboarding/preview/${userId}`, { companyName: data.companyName, products: data.products, targetSpec: res.targetSpec }),
        (result) => patch((prev) => ({ preview: result, chat: { ...prev.chat, preview: [...(prev.chat?.preview || []).filter((m) => !m.fromPreview), ...previewMessages(result)] } })),
        { timeout: 200000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply that change");
      return false;
    }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/onboarding/complete/${userId}`, {
        instanceId: instanceId || null,
        companyName: data.companyName, website: data.website || null, products: data.products,
        sellerIntelligence: data.analysis?.sellerIntelligence || {}, websiteAnalysis: data.analysis?.websiteAnalysis || null,
        targetSpec: data.spec, bestCustomers: data.bestCustomers,
        revenueGoal: Number(data.revenueGoal), goalEndDate: data.goalEndDate,
        averageDealSize: data.averageDealSize ? Number(data.averageDealSize) : null, runFirstHunt: true,
        intakeItems: data.intakeItems || [], mission: data.mission || null,
      });
      patch({ huntJobId: res.huntJobId });
      return res;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your setup");
      return null;
    } finally {
      setBusy(false);
    }
  };

  return { step, setStep, data, patch, patchSpec, ready, job, busy, analyze, runPreview, applyGuidance, complete };
};
